import { CheckCircleOutlined, DeleteOutlined, EditOutlined, PlusOutlined, StarFilled, ThunderboltOutlined } from "@ant-design/icons";
import {
  Alert, Button, Card, Col, DatePicker, Form, Input, Popconfirm,
  Row, Space, Switch, Table, Tag, Tabs, Typography, message, Select
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { transportApi } from "../../api/transportApi";
import { useFyStore } from "../../stores/fyStore";
import { getApiErrorMessage } from "../../utils/apiError";
import DocumentSequenceManagementTab from "./DocumentSequenceManagementTab";
import TransportTaxProfilesTab from "./TransportTaxProfilesTab";
import { StaticDrawer } from "../../shared/components/StaticDrawer";
import { SaveActionButton } from "../../shared/components/SaveActionButton";

// ─── Financial Year Management ───────────────────────────────────────────────

type FyRow = {
  id: number; code: string; startDate: string; endDate: string;
  isActive: boolean; isClosed: boolean; isYearClosed: boolean; createdOn: string;
};

function inferFyForYear(year: number) {
  return {
    code: `${year}-${String(year + 1).slice(2)}`,
    startDate: dayjs(`${year}-04-01`),
    endDate: dayjs(`${year + 1}-03-31`),
  };
}

const FinancialYearsTab = () => {
  const [rows, setRows] = useState<FyRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const { activeFyId, setActiveFy } = useFyStore();

  const load = async () => {
    setLoading(true);
    try {
      const data = await transportApi.listFinancialYears();
      setRows(data as FyRow[]);

      // If no active FY is in store, try to find and set one from the list
      if (!activeFyId || activeFyId === 1) {
        const activeFy = data.find((fy: FyRow) => fy.isActive);
        if (activeFy && activeFyId !== activeFy.id) {
          setActiveFy(activeFy.id, activeFy.code);
        }
      }
    } catch (e) {
      message.error(getApiErrorMessage(e, "Unable to load financial years"));
    } finally { setLoading(false); }
  };

  useEffect(() => { void load(); }, [activeFyId, setActiveFy]);

  const openCreate = () => {
    setEditingId(null);
    form.resetFields();
    const now = dayjs();
    const fyYear = now.month() >= 3 ? now.year() : now.year() - 1;
    const defaults = inferFyForYear(fyYear);
    form.setFieldsValue({ code: defaults.code, startDate: defaults.startDate, endDate: defaults.endDate, isActive: true });
    setDrawerOpen(true);
  };

  const openEdit = (row: FyRow) => {
    setEditingId(row.id);
    form.setFieldsValue({
      code: row.code,
      startDate: dayjs(row.startDate),
      endDate: dayjs(row.endDate),
      isActive: row.isActive,
    });
    setDrawerOpen(true);
  };

  const onSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const payload = {
        code: values.code,
        startDate: (values.startDate as dayjs.Dayjs).toISOString(),
        endDate: (values.endDate as dayjs.Dayjs).toISOString(),
        isActive: values.isActive ?? false,
      };
      let result: FyRow;
      if (editingId) {
        result = await transportApi.updateFinancialYear(editingId, payload);
        message.success("Financial year updated");
      } else {
        result = await transportApi.createFinancialYear(payload);
        message.success("Financial year created");
      }
      if (result.isActive) setActiveFy(result.id, result.code);
      setDrawerOpen(false);
      await load();
    } catch (e) {
      message.error(getApiErrorMessage(e, "Unable to save financial year"));
    } finally { setSaving(false); }
  };

  const onActivate = async (row: FyRow) => {
    try {
      const result = await transportApi.activateFinancialYear(row.id);
      setActiveFy(result.id, result.code);
      message.success(`${row.code} is now the active financial year`);
      await load();
    } catch (e) {
      message.error(getApiErrorMessage(e, "Unable to activate financial year"));
    }
  };

  const quickCreate = () => {
    const now = dayjs();
    const fyYear = now.month() >= 3 ? now.year() : now.year() - 1;
    const defaults = inferFyForYear(fyYear);
    form.setFieldsValue({ code: defaults.code, startDate: defaults.startDate, endDate: defaults.endDate, isActive: true });
    setEditingId(null);
    setDrawerOpen(true);
  };

  const columns: ColumnsType<FyRow> = [
    {
      title: "SN",
      width: 70,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Code", dataIndex: "code", width: 110,
      render: (v: string, row) => (
        <Space size={6}>
          {row.id === activeFyId && <CheckCircleOutlined style={{ color: "#22c55e" }} />}
          <strong>{v}</strong>
        </Space>
      ),
    },
    { title: "Start Date", dataIndex: "startDate", width: 120, render: (v: string) => dayjs(v).format("DD MMM YYYY") },
    { title: "End Date", dataIndex: "endDate", width: 120, render: (v: string) => dayjs(v).format("DD MMM YYYY") },
    {
      title: "Status", width: 160,
      render: (_, row) => (
        <Space size={4}>
          {row.isActive && <Tag color="green">Active</Tag>}
          {row.isClosed || row.isYearClosed ? <Tag color="default">Closed</Tag> : null}
          {!row.isActive && !row.isClosed && !row.isYearClosed && <Tag color="default">Inactive</Tag>}
        </Space>
      ),
    },
    {
      title: "", width: 160,
      render: (_, row) => (
        <Space size={4}>
          {!row.isActive && !row.isClosed && (
            <Popconfirm title={`Set ${row.code} as active year?`} onConfirm={() => void onActivate(row)} okText="Activate">
              <Button size="small" type="primary" ghost>Set Active</Button>
            </Popconfirm>
          )}
          {!row.isClosed && (
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          )}
        </Space>
      ),
    },
  ];

  const hasActive = rows.some(r => r.isActive);

  return (
    <>
      {!hasActive && rows.length === 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="No financial year configured"
          description="You must create at least one financial year before you can add vehicles, consignments, or trips."
          action={
            <Button type="primary" icon={<ThunderboltOutlined />} onClick={quickCreate}>
              Quick Create Current Year
            </Button>
          }
        />
      )}
      {!hasActive && rows.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="No active financial year"
          description="Set one of the years below as active, or create a new one."
        />
      )}

      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add Financial Year</Button>
        </Col>
        <Col>
          <Button icon={<ThunderboltOutlined />} onClick={quickCreate}>Quick Create Current Year</Button>
        </Col>
      </Row>

      <Table
        rowKey="id" size="small" loading={loading} dataSource={rows} columns={columns}
        pagination={false}
        rowClassName={(row) => row.id === activeFyId ? "fy-active-row" : ""}
      />

      <StaticDrawer
        title={editingId ? "Edit Financial Year" : "Add Financial Year"}
        open={drawerOpen} onClose={() => setDrawerOpen(false)} width={480}
        extra={<SaveActionButton saving={saving} idleText={editingId ? "Update" : "Save"} savingText={editingId ? "Updating Financial Year..." : "Saving Financial Year..."} onClick={() => void onSave()} />}
        destroyOnClose
      >
        <Alert
          type="info" showIcon style={{ marginBottom: 16 }}
          message="The active financial year is used for all new entries (vehicles, trips, consignments)."
        />
        <Form form={form} layout="vertical">
          <Form.Item name="code" label="Year Code" rules={[{ required: true, message: "e.g. 2025-26" }]}
            extra="Format: YYYY-YY (e.g. 2025-26)">
            <Input
              placeholder="2025-26"
              onChange={(e) => {
                const v = e.target.value.trim();
                const match = /^(\d{4})-\d{2}$/.exec(v);
                if (match) {
                  const startYear = parseInt(match[1]);
                  form.setFieldsValue(inferFyForYear(startYear));
                }
              }}
            />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="startDate" label="Start Date" rules={[{ required: true }]}>
                <DatePicker style={{ width: "100%" }} format="DD MMM YYYY" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="endDate" label="End Date" rules={[{ required: true }]}>
                <DatePicker style={{ width: "100%" }} format="DD MMM YYYY" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="isActive" label="Set as Active Year" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </StaticDrawer>
    </>
  );
};

// ─── Branch Management ────────────────────────────────────────────────────────

type BranchRow = {
  id: number; branchName: string; branchCode: string;
  city: string; state: string; isHeadOffice: boolean; isActive: boolean;
};

const BranchesTab = () => {
  const [rows, setRows] = useState<BranchRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const load = async (pg = page, q = search) => {
    setLoading(true);
    try {
      const result = await transportApi.listBranches({ page: pg, pageSize: 20 });
      setRows(result.data as BranchRow[]);
      setTotal(result.total);
    } catch (e) {
      message.error(getApiErrorMessage(e, "Unable to load branches"));
    } finally { setLoading(false); }
  };

  useEffect(() => { void load(); }, []);

  const openCreate = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true, isHeadOffice: false });
    setDrawerOpen(true);
  };

  const openEdit = (row: BranchRow) => {
    setEditingId(row.id);
    form.setFieldsValue(row);
    setDrawerOpen(true);
  };

  const onSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (editingId) {
        await transportApi.updateBranch(editingId, values);
        message.success("Branch updated");
      } else {
        await transportApi.createBranch(values);
        message.success("Branch created");
      }
      setDrawerOpen(false);
      await load();
    } catch (e) {
      message.error(getApiErrorMessage(e, "Unable to save branch"));
    } finally { setSaving(false); }
  };

  const onDelete = async (id: number) => {
    try {
      await transportApi.deleteBranch(id);
      message.success("Branch deactivated");
      await load();
    } catch (e) {
      message.error(getApiErrorMessage(e, "Unable to deactivate branch"));
    }
  };

  const columns: ColumnsType<BranchRow> = [
    { title: "SN", width: 70, render: (_, __, index) => ((page - 1) * 20) + index + 1 },
    { title: "Code", dataIndex: "branchCode", width: 90 },
    {
      title: "Branch / Station Name", dataIndex: "branchName",
      render: (v: string, row) => (
        <Space size={6}>
          {row.isHeadOffice && <StarFilled style={{ color: "#f59e0b", fontSize: 12 }} />}
          <span>{v}</span>
        </Space>
      ),
    },
    { title: "City", dataIndex: "city", width: 130 },
    { title: "State", dataIndex: "state", width: 110 },
    {
      title: "Status", dataIndex: "isActive", width: 90,
      render: (v: boolean) => <Tag color={v ? "green" : "default"}>{v ? "Active" : "Inactive"}</Tag>,
    },
    {
      title: "", width: 100,
      render: (_, row) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          <Popconfirm title="Deactivate branch?" onConfirm={() => void onDelete(row.id)} okButtonProps={{ danger: true }} okText="Deactivate">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col flex="300px">
          <Input.Search
            placeholder="Search branch / station, code, city..."
            allowClear value={search}
            onChange={(e) => setSearch(e.target.value)}
            onSearch={(v) => { setSearch(v); void load(1, v); }}
          />
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add Branch</Button>
        </Col>
      </Row>

      <Table
        rowKey="id" size="small" loading={loading} dataSource={rows} columns={columns}
        pagination={{
          current: page, total, pageSize: 20, showSizeChanger: false,
          showTotal: (t) => `${t} branches`,
          onChange: (p) => { setPage(p); void load(p, search); },
        }}
      />

      <StaticDrawer
        title={editingId ? "Edit Branch" : "Add Branch"}
        open={drawerOpen} onClose={() => setDrawerOpen(false)} width={520}
        extra={<SaveActionButton saving={saving} idleText={editingId ? "Update" : "Save"} savingText={editingId ? "Updating Branch..." : "Saving Branch..."} onClick={() => void onSave()} />}
        destroyOnClose
      >
        <Form form={form} layout="vertical" initialValues={{ isActive: true, isHeadOffice: false }}>
          <Row gutter={12}>
            <Col span={16}>
              <Form.Item name="branchName" label="Branch / Station Name" rules={[{ required: true }]}>
                <Input placeholder="e.g. Mumbai Hub" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="branchCode" label="Code" extra="Auto if blank">
                <Input placeholder="MUM" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="city" label="City">
                <Input placeholder="Mumbai" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="state" label="State">
                <Input placeholder="Maharashtra" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="isHeadOffice" label="Head Office" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            {editingId && (
              <Col span={12}>
                <Form.Item name="isActive" label="Active" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Form>
      </StaticDrawer>
    </>
  );
};

// ─── Locations Overview ───────────────────────────────────────────────────────

const LocationsTab = () => {
  const [locations, setLocations] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const load = async (q = "") => {
    setLoading(true);
    try {
      const items = await transportApi.searchLocationSuggestions(q);
      setLocations(items ?? []);
    } catch (e) {
      message.error(getApiErrorMessage(e, "Unable to load locations"));
    } finally { setLoading(false); }
  };

  useEffect(() => { void load(); }, []);

  const filtered = search
    ? locations.filter(x => x.toLowerCase().includes(search.toLowerCase()))
    : locations;

  return (
    <>
      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col flex="300px">
          <Input.Search
            placeholder="Filter locations..."
            allowClear value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Col>
        <Col>
          <Typography.Text type="secondary" style={{ lineHeight: "32px" }}>
            {filtered.length} location{filtered.length !== 1 ? "s" : ""} derived from consignments and branches
          </Typography.Text>
        </Col>
      </Row>

      <Table
        rowKey={(r) => r}
        size="small"
        loading={loading}
        dataSource={filtered}
        pagination={{ pageSize: 20, showSizeChanger: false }}
        columns={[
          {
            title: "SN",
            width: 70,
            render: (_, __, index) => index + 1,
          },
          {
            title: "City / Location", dataIndex: undefined,
            render: (v: string) => <Typography.Text>{v}</Typography.Text>,
          },
        ]}
        locale={{ emptyText: "No locations found. Locations are automatically populated from consignment origins and destinations, and branch cities." }}
      />

      <Typography.Text type="secondary" style={{ display: "block", marginTop: 16, fontSize: 12 }}>
        To add new locations: create branches with city names above, or they will appear automatically as you create consignments with new origins/destinations.
      </Typography.Text>
    </>
  );
};

// ─── Consignee Master ─────────────────────────────────────────────────────────

type PartyRow = {
  id: number; name: string; code: string; type: string;
  gSTNumber?: string; phone?: string; addressLine1?: string;
  city?: string; state?: string; isActive: boolean;
};

const ConsigneeMasterTab = () => {
  const [rows, setRows] = useState<PartyRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const load = async (pg = page, q = search) => {
    setLoading(true);
    try {
      const result = await transportApi.listPartiesByType("Customer", { page: pg, pageSize: 20, q: q || undefined });
      setRows(result.data as PartyRow[]);
      setTotal(result.total);
    } catch (e) {
      message.error(getApiErrorMessage(e, "Unable to load consignees"));
    } finally { setLoading(false); }
  };

  useEffect(() => { void load(); }, []);

  const openCreate = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({ type: "Customer", isActive: true });
    setDrawerOpen(true);
  };

  const openEdit = (row: PartyRow) => {
    setEditingId(row.id);
    form.setFieldsValue({
      name: row.name, code: row.code, gSTNumber: row.gSTNumber,
      phone: row.phone, addressLine1: row.addressLine1,
      city: row.city, state: row.state, isActive: row.isActive
    });
    setDrawerOpen(true);
  };

  const onSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const payload = { ...values, type: "Customer" };
      if (editingId) {
        await transportApi.updateParty(editingId, payload);
        message.success("Consignee updated");
      } else {
        await transportApi.createParty(payload);
        message.success("Consignee created");
      }
      setDrawerOpen(false);
      await load();
    } catch (e) {
      message.error(getApiErrorMessage(e, "Unable to save consignee"));
    } finally { setSaving(false); }
  };

  const onDeactivate = async (id: number) => {
    try {
      await transportApi.deleteParty(id);
      message.success("Consignee deactivated");
      await load();
    } catch (e) {
      message.error(getApiErrorMessage(e, "Unable to deactivate consignee"));
    }
  };

  const columns: ColumnsType<PartyRow> = [
    { title: "SN", width: 55, render: (_, __, i) => ((page - 1) * 20) + i + 1 },
    { title: "ID", dataIndex: "id", width: 60 },
    { title: "Name", dataIndex: "name" },
    { title: "Code", dataIndex: "code", width: 90 },
    { title: "GST No", dataIndex: "gSTNumber", width: 160 },
    { title: "State", dataIndex: "state", width: 110 },
    { title: "Mobile", dataIndex: "phone", width: 120 },
    {
      title: "Active", dataIndex: "isActive", width: 80,
      render: (v: boolean) => <Tag color={v ? "green" : "default"}>{v ? "Y" : "N"}</Tag>
    },
    {
      title: "", width: 100,
      render: (_, row) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          <Popconfirm title="Deactivate this consignee?" onConfirm={() => void onDeactivate(row.id)} okButtonProps={{ danger: true }} okText="Deactivate">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <>
      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col flex="300px">
          <Input.Search
            placeholder="Search by name, code, GST..."
            allowClear
            value={search}
            onChange={e => setSearch(e.target.value)}
            onSearch={v => { setSearch(v); void load(1, v); }}
          />
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add Consignee</Button>
        </Col>
      </Row>

      <Table
        rowKey="id" size="small" loading={loading} dataSource={rows} columns={columns}
        pagination={{
          current: page, total, pageSize: 20, showSizeChanger: false,
          showTotal: t => `${t} consignees`,
          onChange: p => { setPage(p); void load(p, search); }
        }}
      />

      <StaticDrawer
        title={editingId ? "Edit Consignee" : "Add Consignee"}
        width={520}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        extra={
          <SaveActionButton
            saving={saving}
            idleText={editingId ? "Update" : "Save"}
            savingText={editingId ? "Updating..." : "Saving..."}
            onClick={() => void onSave()}
          />
        }
        destroyOnClose
      >
        <Form form={form} layout="vertical" initialValues={{ isActive: true }}>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: "Name is required" }]}>
            <Input placeholder="Consignee business name" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="code" label="Code" extra="Auto-generated if blank">
                <Input placeholder="e.g. CONS001" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="phone" label="Mobile No">
                <Input placeholder="10-digit mobile" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="addressLine1" label="Address" rules={[{ required: true, message: "Address is required" }]}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="city" label="City">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="state" label="State">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="gSTNumber" label="GST No">
            <Input placeholder="15-character GSTIN" maxLength={15} />
          </Form.Item>
          {editingId && (
            <Form.Item name="isActive" label="Active" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}
        </Form>
      </StaticDrawer>
    </>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export const MasterDataPage = () => {
  const [activeTab, setActiveTab] = useState("financial-years");

  return (
    <Card className="panel-card">
      <Typography.Title level={3} style={{ margin: "0 0 16px" }}>Master Data</Typography.Title>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          { key: "financial-years", label: "Financial Years", children: <FinancialYearsTab /> },
          { key: "document-sequences", label: "Document Sequences", children: <DocumentSequenceManagementTab /> },
          { key: "tax-profiles", label: "Tax Profiles", children: <TransportTaxProfilesTab /> },
          { key: "branches", label: "Branches / Stations", children: <BranchesTab /> },
          { key: "locations", label: "Locations", children: <LocationsTab /> },
          { key: "consignees", label: "Consignee Master", children: <ConsigneeMasterTab /> },
        ]}
      />
    </Card>
  );
};
